(() => {
  'use strict';

  if (window.SMIBSoftNavigation) return;

  const MAIN_SELECTOR = '#MainContent';
  const HEADER_SELECTOR = '#header-group';
  const HISTORY_KEY = 'smibSoftNavigation';
  const loadedExternalScripts = new Set(
    Array.from(document.scripts)
      .map((script) => script.src)
      .filter(Boolean)
      .map((src) => new URL(src, window.location.href).href)
  );

  let activeController = null;
  let navigating = false;

  function isDesignMode() {
    return Boolean(window.Shopify?.designMode || document.documentElement.classList.contains('shopify-design-mode'));
  }

  function isModifiedClick(event) {
    return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  }

  function isExcludedPath(pathname) {
    return [
      '/checkout',
      '/checkouts',
      '/account',
      '/challenge',
      '/payments',
      '/wallets',
      '/admin',
    ].some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  }

  function shouldHandleLink(link, event) {
    if (!link || isDesignMode() || isModifiedClick(event)) return false;
    if (event.defaultPrevented) return false;
    if (link.hasAttribute('download') || link.hasAttribute('data-no-soft-nav')) return false;
    if (link.target && link.target.toLowerCase() !== '_self') return false;

    const rawHref = link.getAttribute('href');
    if (!rawHref || /^(#|mailto:|tel:|sms:|javascript:)/i.test(rawHref)) return false;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (_error) {
      return false;
    }

    if (!['http:', 'https:'].includes(url.protocol)) return false;
    if (url.origin !== window.location.origin) return false;
    if (isExcludedPath(url.pathname)) return false;

    const current = new URL(window.location.href);
    if (
      url.pathname === current.pathname &&
      url.search === current.search &&
      url.hash !== current.hash
    ) {
      return false;
    }

    if (url.href === current.href) return false;

    return true;
  }

  function getScrollContainer() {
    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper instanceof HTMLElement) {
      const style = getComputedStyle(pageWrapper);
      if (/(auto|scroll)/.test(style.overflowY) && pageWrapper.scrollHeight > pageWrapper.clientHeight) {
        return pageWrapper;
      }
    }

    return document.scrollingElement || document.documentElement;
  }

  function getScrollPosition() {
    const container = getScrollContainer();
    return container === document.scrollingElement || container === document.documentElement
      ? window.scrollY
      : container.scrollTop;
  }

  function scrollToPosition(top) {
    const target = Number.isFinite(Number(top)) ? Number(top) : 0;
    const container = getScrollContainer();

    if (container === document.scrollingElement || container === document.documentElement) {
      window.scrollTo(0, target);
    } else {
      container.scrollTop = target;
    }
  }

  function saveCurrentHistoryState() {
    const currentState = history.state && typeof history.state === 'object' ? history.state : {};
    history.replaceState(
      {
        ...currentState,
        [HISTORY_KEY]: true,
        smibScrollTop: getScrollPosition(),
      },
      '',
      window.location.href
    );
  }

  function setLoadingState(loading) {
    navigating = loading;
    document.documentElement.toggleAttribute('data-smib-soft-navigating', loading);
    document.querySelector(MAIN_SELECTOR)?.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  function syncHead(incomingDocument) {
    document.title = incomingDocument.title || document.title;

    const description = incomingDocument.head.querySelector('meta[name="description"]');
    const currentDescription = document.head.querySelector('meta[name="description"]');
    if (description && currentDescription) currentDescription.setAttribute('content', description.content);
    else if (description && !currentDescription) document.head.appendChild(description.cloneNode(true));
    else currentDescription?.remove();

    const canonical = incomingDocument.head.querySelector('link[rel="canonical"]');
    const currentCanonical = document.head.querySelector('link[rel="canonical"]');
    if (canonical && currentCanonical) currentCanonical.setAttribute('href', canonical.href);
    else if (canonical && !currentCanonical) document.head.appendChild(canonical.cloneNode(true));
    else currentCanonical?.remove();

    incomingDocument.head.querySelectorAll('link[rel="stylesheet"][href]').forEach((link) => {
      const href = new URL(link.href, window.location.href).href;
      const exists = Array.from(document.styleSheets).some((sheet) => sheet.href === href) ||
        Array.from(document.head.querySelectorAll('link[rel="stylesheet"][href]')).some((currentLink) => currentLink.href === href);
      if (!exists) document.head.appendChild(link.cloneNode(true));
    });

    incomingDocument.head.querySelectorAll('script[src]').forEach((script) => {
      loadExternalScript(script);
    });
  }

  function loadExternalScript(sourceScript) {
    const src = sourceScript.src ? new URL(sourceScript.src, window.location.href).href : '';
    if (!src || loadedExternalScripts.has(src)) return;

    loadedExternalScripts.add(src);

    const script = document.createElement('script');
    Array.from(sourceScript.attributes).forEach((attribute) => {
      script.setAttribute(attribute.name, attribute.value);
    });
    script.src = src;
    document.head.appendChild(script);
  }

  function activateScripts(root) {
    root.querySelectorAll('script').forEach((oldScript) => {
      const type = (oldScript.getAttribute('type') || '').trim().toLowerCase();
      if (['application/ld+json', 'application/json', 'importmap'].includes(type)) return;

      if (oldScript.src) {
        loadExternalScript(oldScript);
        return;
      }

      const script = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attribute) => {
        script.setAttribute(attribute.name, attribute.value);
      });
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });
  }

  function dispatchSectionEvent(root, eventName) {
    const sections = [];
    if (root.matches?.('.shopify-section')) sections.push(root);
    sections.push(...root.querySelectorAll?.('.shopify-section') || []);

    sections.forEach((section) => {
      section.dispatchEvent(new CustomEvent(eventName, { bubbles: true }));
    });
  }

  function replaceRegion(selector, incomingDocument) {
    const current = document.querySelector(selector);
    const incoming = incomingDocument.querySelector(selector);
    if (!current || !incoming) throw new Error(`Missing navigation region: ${selector}`);

    dispatchSectionEvent(current, 'shopify:section:unload');

    const replacement = document.importNode(incoming, true);
    current.replaceWith(replacement);
    activateScripts(replacement);
    dispatchSectionEvent(replacement, 'shopify:section:load');

    return replacement;
  }

  function syncDocumentContext(incomingDocument) {
    document.documentElement.lang = incomingDocument.documentElement.lang || document.documentElement.lang;
    if (incomingDocument.documentElement.dir) document.documentElement.dir = incomingDocument.documentElement.dir;
    else document.documentElement.removeAttribute('dir');

    document.body.className = incomingDocument.body.className;

    const main = document.querySelector(MAIN_SELECTOR);
    if (window.Theme?.template && main?.dataset.template) {
      window.Theme.template.name = main.dataset.template;
    }
  }

  function closeTransientUi() {
    document.querySelectorAll('dialog[open]').forEach((dialog) => {
      try {
        dialog.close();
      } catch (_error) {
        dialog.removeAttribute('open');
      }
    });

    document.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
  }

  function applyDocument(incomingDocument) {
    syncHead(incomingDocument);
    closeTransientUi();
    replaceRegion(HEADER_SELECTOR, incomingDocument);
    replaceRegion(MAIN_SELECTOR, incomingDocument);
    syncDocumentContext(incomingDocument);
  }

  async function fetchDocument(url, signal) {
    const response = await fetch(url.href, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html,application/xhtml+xml',
        'X-Requested-With': 'SMIBSoftNavigation',
      },
      signal,
    });

    if (!response.ok) throw new Error(`Navigation request failed: ${response.status}`);

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('text/html')) throw new Error('Navigation response was not HTML');

    const finalUrl = new URL(response.url || url.href, window.location.href);
    if (finalUrl.origin !== window.location.origin || isExcludedPath(finalUrl.pathname)) {
      throw new Error('Navigation redirected to an excluded destination');
    }

    const html = await response.text();
    const incomingDocument = new DOMParser().parseFromString(html, 'text/html');

    if (!incomingDocument.querySelector(MAIN_SELECTOR) || !incomingDocument.querySelector(HEADER_SELECTOR)) {
      throw new Error('Navigation response is missing required theme regions');
    }

    return { incomingDocument, finalUrl };
  }

  async function navigate(target, options = {}) {
    if (isDesignMode()) {
      window.location.assign(target.href);
      return;
    }

    const url = target instanceof URL ? target : new URL(target, window.location.href);
    const isPopState = options.popstate === true;
    const restoreScroll = options.restoreScroll;

    activeController?.abort();
    activeController = new AbortController();
    const controller = activeController;

    if (!isPopState) saveCurrentHistoryState();
    setLoadingState(true);

    try {
      const { incomingDocument, finalUrl } = await fetchDocument(url, controller.signal);
      if (controller.signal.aborted) return;

      if (!isPopState) {
        history.pushState(
          {
            [HISTORY_KEY]: true,
            smibScrollTop: 0,
          },
          '',
          finalUrl.href
        );
      }

      applyDocument(incomingDocument);
      if (controller.signal.aborted) return;

      if (finalUrl.hash) {
        requestAnimationFrame(() => {
          const targetElement = document.getElementById(decodeURIComponent(finalUrl.hash.slice(1)));
          if (targetElement) targetElement.scrollIntoView();
          else scrollToPosition(restoreScroll ?? 0);
        });
      } else {
        requestAnimationFrame(() => scrollToPosition(restoreScroll ?? 0));
      }

      document.dispatchEvent(
        new CustomEvent('smib:page:loaded', {
          detail: {
            url: finalUrl.href,
            popstate: isPopState,
          },
        })
      );

      window.SMIB?.updateHeaderLayout?.();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[SMIB navigation] Falling back to a normal page load.', error);
      window.location.assign(url.href);
      return;
    } finally {
      if (activeController === controller) activeController = null;
      setLoadingState(false);
    }
  }

  document.addEventListener('click', (event) => {
    const link = event.target?.closest?.('a[href]');
    if (!shouldHandleLink(link, event)) return;

    event.preventDefault();
    navigate(new URL(link.href, window.location.href));
  });

  window.addEventListener('popstate', (event) => {
    if (!event.state?.[HISTORY_KEY]) return;

    navigate(new URL(window.location.href), {
      popstate: true,
      restoreScroll: event.state?.smibScrollTop ?? 0,
    });
  });

  function start() {
    if (isDesignMode()) return;

    const currentState = history.state && typeof history.state === 'object' ? history.state : {};
    history.replaceState(
      {
        ...currentState,
        [HISTORY_KEY]: true,
        smibScrollTop: getScrollPosition(),
      },
      '',
      window.location.href
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.SMIBSoftNavigation = Object.freeze({
    navigate(url) {
      return navigate(new URL(url, window.location.href));
    },
    get navigating() {
      return navigating;
    },
  });
})();
